import { Box, Flex, Heading, useColorMode, Skeleton, SkeletonText, SkeletonCircle, Text } from '@chakra-ui/react';
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import WeeklyRevenue from 'views/admin/default/components/WeeklyRevenue';
import TopFiveSalesAndPurchase from './components/TopFiveSalesAndPurchase';
import BankAccountsCard from './components/BankAccountsCard';
import { useSelector, useDispatch } from 'react-redux';
import { useUser } from 'contexts/UserContext';
import {
   getGrowthRate,
   getTopFivePartyWisePurchase,
   getTopFivePartyWiseSales,
   getCashAndBankBalance,
   getTotalSalesAndPurchases,
   getProfitabilityAnalysisByCustomer,
   getCashFlowAnalysis,
   getTransactionSizeDistributionAnalysis,
   getAgingAnalysis,
   getYearOverYearComparison,
   getCustomerConcentrationAnalysis,
   getCityWiseSales,
   getSalesPaymentReceiveReport,
   getPurchasePaidReport,
   UpdateBulkStatus,
} from '../../../redux/actions/dashboard';
import { deleteOverAdjusted, deleteExtraOutAdj } from '../../../redux/actions/outAdjustment';
import { deleteLedgerEntries } from '../../../redux/actions/ledger';
import Rojmel from './components/Rojmel';
import GrowthRateTable from './components/GrowthRateTable';
import SalesVsPurchaseChart from './components/SalesVsPurchaseChart';
import ItcLedger from './components/ItcLedger';
import ProfitabilityByCustomerChart from './components/ProfitabilityByCustomerChart';
import CashFlowAnalysisChart from './components/CashFlowAnalysisChart';
import TransactionSizeDistributionChart from './components/TransactionSizeDistributionChart';
import AgingBarChart from './components/AgingBarChart';
import CustomerConcentrationChart from './components/CustomerConcentrationChart';
import SalesPaymentRadialChart from './components/SalesPaymentRadialChart';
import PurchasePaidChart from './components/PurchasePaidChart';
import { RepeatIcon } from '@chakra-ui/icons';
import { BsEye } from 'react-icons/bs';
import { postColumnsHideShow } from '../../../redux/actions/utility';
import {
   Building2,
   Check,
   CreditCard,
   FileText,
   Package,
   RotateCcw,
   ShoppingCart,
   Undo2,
   User,
   Wallet,
   X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAccountForDashboard } from '../../../redux/actions/account';
import { useGlobal } from '../../../contexts';
import PartyForm from './PartyForm';
import UserDetailsPage from 'components/ZeronineWebsite/userDetails';
import { updateUserHistory } from '../../../redux/actions/user';
import { getCompanies } from '../../../redux/actions/company';

// Skeleton Loader Components
const StatsSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <div className="group relative overflow-hidden rounded-2xl transition-all duration-500">
         <div
            className={`relative p-5 sm:p-6 flex flex-col h-full ${
               isDark ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-white border border-gray-200'
            }`}>
            <div className="flex items-center justify-between mb-4">
               <SkeletonCircle
                  size="48px"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
               <Skeleton
                  height="20px"
                  width="40px"
                  borderRadius="full"
                  startColor={isDark ? 'gray.600' : 'gray.200'}
                  endColor={isDark ? 'gray.700' : 'gray.300'}
               />
            </div>
            <div className="flex flex-col flex-grow justify-between space-y-4">
               <div className="space-y-2">
                  <Skeleton
                     height="16px"
                     width="60px"
                     startColor={isDark ? 'gray.600' : 'gray.200'}
                     endColor={isDark ? 'gray.700' : 'gray.300'}
                  />
                  <Skeleton
                     height="28px"
                     width="100px"
                     startColor={isDark ? 'gray.600' : 'gray.200'}
                     endColor={isDark ? 'gray.700' : 'gray.300'}
                  />
               </div>
               <div className="space-y-2">
                  <Skeleton
                     height="8px"
                     width="100%"
                     borderRadius="full"
                     startColor={isDark ? 'gray.600' : 'gray.200'}
                     endColor={isDark ? 'gray.700' : 'gray.300'}
                  />
               </div>
            </div>
         </div>
      </div>
   );
};

const ChartSkeleton = ({ height = '400px' }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <Box
         height={height}
         className={`rounded-xl p-4 ${
            isDark ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-white border border-gray-200'
         }`}>
         <Skeleton
            height="24px"
            width="200px"
            mb={4}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
         <Skeleton
            height="calc(100% - 40px)"
            width="100%"
            borderRadius="lg"
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
      </Box>
   );
};

const TableSkeleton = ({ height = '400px', rowCount = 5 }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <Box
         height={height}
         className={`rounded-xl p-4 ${
            isDark ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-white border border-gray-200'
         }`}>
         <Skeleton
            height="24px"
            width="200px"
            mb={4}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
         <Box className="space-y-3">
            {Array.from({ length: rowCount }).map((_, index) => (
               <Box key={index} className="flex items-center space-x-3">
                  <SkeletonCircle
                     size="32px"
                     startColor={isDark ? 'gray.600' : 'gray.200'}
                     endColor={isDark ? 'gray.700' : 'gray.300'}
                  />
                  <Box flex={1}>
                     <Skeleton
                        height="16px"
                        width="70%"
                        mb={2}
                        startColor={isDark ? 'gray.600' : 'gray.200'}
                        endColor={isDark ? 'gray.700' : 'gray.300'}
                     />
                     <Skeleton
                        height="14px"
                        width="50%"
                        startColor={isDark ? 'gray.600' : 'gray.200'}
                        endColor={isDark ? 'gray.700' : 'gray.300'}
                     />
                  </Box>
                  <Skeleton
                     height="20px"
                     width="80px"
                     startColor={isDark ? 'gray.600' : 'gray.200'}
                     endColor={isDark ? 'gray.700' : 'gray.300'}
                  />
               </Box>
            ))}
         </Box>
      </Box>
   );
};

const CardGridSkeleton = ({ count = 6 }) => {
   return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5 sm:gap-6 lg:gap-7">
         {Array.from({ length: count }).map((_, index) => (
            <StatsSkeleton key={index} />
         ))}
      </div>
   );
};

// Custom hook for scroll-triggered loading
const useScrollLoader = (loadMore, threshold = 100) => {
   const loaderRef = useRef(null);
   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      const observer = new IntersectionObserver(
         (entries) => {
            const firstEntry = entries[0];
            if (firstEntry.isIntersecting && !isLoading) {
               setIsLoading(true);
               Promise.resolve(loadMore()).finally(() => {
                  setIsLoading(false);
               });
            }
         },
         { threshold: 0.1 },
      );

      const currentLoader = loaderRef.current;
      if (currentLoader) {
         observer.observe(currentLoader);
      }

      return () => {
         if (currentLoader) {
            observer.unobserve(currentLoader);
         }
      };
   }, [loadMore, isLoading]);

   return { loaderRef, isLoading };
};

// Section Wrapper with lazy loading capability
const SectionWrapper = ({
   title,
   children,
   onRefresh,
   isLoading,
   onVisible,
   textColor,
   isVisible = true,
   skeletonComponent,
}) => {
   const sectionRef = useRef(null);
   const [hasBeenVisible, setHasBeenVisible] = useState(false);
   const [isSectionLoading, setIsSectionLoading] = useState(true);

   useEffect(() => {
      if (!onVisible || hasBeenVisible || !isVisible) return;

      const observer = new IntersectionObserver(
         ([entry]) => {
            if (entry.isIntersecting) {
               setHasBeenVisible(true);
               setIsSectionLoading(true);
               Promise.resolve(onVisible()).finally(() => {
                  setIsSectionLoading(false);
               });
            }
         },
         { threshold: 0.1 },
      );

      if (sectionRef.current) {
         observer.observe(sectionRef.current);
      }

      return () => {
         if (sectionRef.current) {
            observer.unobserve(sectionRef.current);
         }
      };
   }, [onVisible, hasBeenVisible, isVisible]);

   if (!isVisible) return null;

   return (
      <Box mb={6} ref={sectionRef}>
         <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Heading size="md" color={textColor}>
               {title}
            </Heading>
         </Flex>
         {isSectionLoading || isLoading ? skeletonComponent : children}
      </Box>
   );
};

export default function UserReports() {
   const dispatch = useDispatch();
   const { user } = useUser();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   // const { accounts: accountData } = useAccount();

   // Helper function to get initial date range from localStorage
   const getInitialDateRange = () => {
      const fyStartDate = localStorage.getItem('selectedFy_start_date');
      const fyEndDate = localStorage.getItem('selectedFy_end_date');

      const formatDate = (dateStr) => {
         if (!dateStr) return null;
         const parts = dateStr.split('/');
         if (parts.length === 3) {
            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
         }
         return null;
      };

      const fromDate = formatDate(fyStartDate) || '2025-04-01';
      const toDate = formatDate(fyEndDate) || '2026-03-31';

      return { fromDate, toDate };
   };

   const initialDateRange = getInitialDateRange();
   const companyList = useSelector((state) => state.company.list);
   const comapnySetting = companyList.find((comp) => comp.id === user?.loginhistory?.companyId);

   // Date ranges for different sections
   const [showDrawer, setShowDrawer] = useState(false);
   const [datePartyWiseSalesRange, setPartyWiseSalesDateRange] = useState(initialDateRange);
   const [topSalesDateRange, setTopSalesDateRange] = useState(initialDateRange);
   const [topPurchaseDateRange, setTopPurchaseDateRange] = useState(initialDateRange);
   const [financialAnalysisDateRange, setFinancialAnalysisDateRange] = useState(initialDateRange);
   const [performanceAnalysisDateRange, setPerformanceAnalysisDateRange] = useState(initialDateRange);
   const [gstInfo, setGstInfo] = useState(initialDateRange);
   const [isPartyFormOpen, setIsPartyFormOpen] = useState(false);
   const [partyData, setPartyData] = useState(null);
   const [formType, setFormType] = useState('');
   const { userGlobalSettingsData } = useGlobal();
   const globalSetting = userGlobalSettingsData?.[0]?.askCompany?.account;
   const shouldShowCompanyDropdown = globalSetting || false;
   const [showCompanyActivationModal, setShowCompanyActivationModal] = useState(false);
   const [selectedCompanies, setSelectedCompanies] = useState([]);

   // Loading states for different sections
   const [isRefreshing, setIsRefreshing] = useState({
      all: false,
      topSales: false,
      topPurchase: false,
      financial: false,
      performance: false,
      GSTInfo: false,
   });

   // Track which sections have been loaded
   const [loadedSections, setLoadedSections] = useState({
      topSales: false,
      financialOverview: false,
      growthAnalysis: false,
      GSTInfo: false,
      performanceAnalysis: false,
   });

   // Track initial loading
   const [isInitialLoading, setIsInitialLoading] = useState(true);

   const accountData = useSelector((state) => state.account.dashboardAccounts || []);

   // Redux selectors
   const topFivePartyWiseSales = useSelector((state) => state?.dashboard?.topFivePartyWiseSales, []);
   const growthRate = useSelector((state) => state?.dashboard?.growthRate, []);
   const cashAndBankBalance = useSelector((state) => state?.dashboard?.cashAndBankBalance, []);
   const totalSalesAndPurchases = useSelector((state) => state?.dashboard?.totalSalesAndPurchases, []);
   const profitabilityAnalysisByCustomer = useSelector(
      (state) => state?.dashboard?.profitabilityAnalysisByCustomer,
      [],
   );
   const cashFlowAnalysis = useSelector((state) => state?.dashboard?.cashFlowAnalysis, []);
   const transactionSizeDistributionAnalysis = useSelector(
      (state) => state?.dashboard?.transactionSizeDistributionAnalysis,
      [],
   );
   const agingAnalysis = useSelector((state) => state?.dashboard?.agingAnalysis, []);
   const yearOverYearComparison = useSelector((state) => state?.dashboard?.yearOverYearComparison, []);
   const customerConcentrationAnalysis = useSelector((state) => state?.dashboard?.customerConcentrationAnalysis, []);
   const cityWiseSales = useSelector((state) => state?.dashboard?.cityWiseSales, []);
   const salesPaymentReceiveReport = useSelector((state) => state?.dashboard?.salesPaymentReceiveReport, []);
   const purchasePaidReport = useSelector((state) => state?.dashboard?.purchasePaidReport, []);
   const DEFAULT_COLUMN_VISIBILITY = {
      topSales: true,
      topPurchase: true,
      rojmel: true,
      bankAccounts: true,
      whatsNew: true,
      growthRate: true,
      totalSaleVsPurchase: true,
      profitabilityByCustomer: true,
      cashFlowAnalysis: true,
      transactionSizeDistribution: true,
      agingAnalysis: true,
      cityWiseSalesAnalysis: true,
      purchasePaidReport: true,
      salesPaymentReceiveReport: true,
      GSTInfo: true,
   };

   // Create a mapping object for display names
   const COLUMN_DISPLAY_NAMES = {
      topSales: 'Top Sales',
      topPurchase: 'Top Purchase',
      rojmel: 'Rojmel',
      bankAccounts: 'Bank Accounts',
      whatsNew: "What's New",
      growthRate: 'Growth Rate',
      totalSaleVsPurchase: 'Total Sale vs Purchase',
      profitabilityByCustomer: 'Profitability by Customer',
      cashFlowAnalysis: 'Cash Flow Analysis',
      transactionSizeDistribution: 'Transaction Size Distribution',
      agingAnalysis: 'Aging Analysis',
      cityWiseSalesAnalysis: 'City Wise Sales Analysis',
      purchasePaidReport: 'Purchase Paid Report',
      salesPaymentReceiveReport: 'Sales Payment Receive Report',
      GSTInfo: 'GST Info',
   };
   const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
   const companyId = Number(localStorage.getItem('companyId'));

   const [columnVisibility, setColumnVisibility] = useState(() => {
      const mergeWithDefaults = (existingVisibility) => {
         return {
            ...DEFAULT_COLUMN_VISIBILITY,
            ...existingVisibility,
         };
      };

      if (user?.viewJson?.length && user?.viewJson[0]?.dashboard) {
         return mergeWithDefaults(user?.viewJson[0].dashboard);
      }

      return DEFAULT_COLUMN_VISIBILITY;
   });

   useEffect(() => {
      localStorage.setItem('dashboardVisibility', JSON.stringify(columnVisibility));
   }, [columnVisibility]);

   useEffect(() => {
      if (user?.viewJson?.length) {
         const companySettings = user.viewJson.find((item) => item.companyId === companyId);
         if (companySettings?.cash) {
            const mergedVisibility = {
               ...DEFAULT_COLUMN_VISIBILITY,
               ...companySettings.cash,
            };
            setColumnVisibility(mergedVisibility);
         }
      }
   }, [user, companyId]);

   // API call functions
   useEffect(() => {
      // Initial load of critical data
      if (user?.loginhistory?.companyId) {
         // Call these APIs immediately on component mount
         Promise.all([
            dispatch(
               getTopFivePartyWiseSales({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: topSalesDateRange.fromDate,
                  toDate: topSalesDateRange.toDate,
               }),
            ),
            dispatch(
               getCashAndBankBalance({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: financialAnalysisDateRange.fromDate,
                  toDate: financialAnalysisDateRange.toDate,
               }),
            ),
         ]).finally(() => {
            setIsInitialLoading(false);
         });
         // Also mark these sections as loaded so they won't trigger again
         setLoadedSections((prev) => ({
            ...prev,
            topSales: true,
            topPurchase: true,
         }));
      }
   }, [user?.loginhistory?.companyId, dispatch]);

   const fetchTopSalesData = useCallback(async () => {
      if (!user?.loginhistory?.companyId) return;

      setIsRefreshing((prev) => ({ ...prev, topSales: true }));
      try {
         await dispatch(
            getTopFivePartyWiseSales({
               companyId: user?.loginhistory?.companyId,
               fromDate: topSalesDateRange.fromDate,
               toDate: topSalesDateRange.toDate,
            }),
         );
      } finally {
         setIsRefreshing((prev) => ({ ...prev, topSales: false }));
      }
   }, [topSalesDateRange.fromDate, topSalesDateRange.toDate, dispatch, user?.loginhistory?.companyId]);

   const fetchTopPurchaseData = useCallback(async () => {
      if (!user?.loginhistory?.companyId) return;

      setIsRefreshing((prev) => ({ ...prev, topPurchase: true }));
      try {
         await dispatch(
            getTopFivePartyWisePurchase({
               companyId: user?.loginhistory?.companyId,
               fromDate: topPurchaseDateRange.fromDate,
               toDate: topPurchaseDateRange.toDate,
            }),
         );
      } finally {
         setIsRefreshing((prev) => ({ ...prev, topPurchase: false }));
      }
   }, [topPurchaseDateRange.fromDate, topPurchaseDateRange.toDate, dispatch, user?.loginhistory?.companyId]);

   const fetchFinancialAnalysisData = useCallback(async () => {
      if (!user?.loginhistory?.companyId) return;

      setIsRefreshing((prev) => ({ ...prev, financial: true }));
      try {
         await Promise.all([
            dispatch(
               getGrowthRate({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: financialAnalysisDateRange.fromDate,
                  toDate: financialAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getCashAndBankBalance({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: financialAnalysisDateRange.fromDate,
                  toDate: financialAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getTotalSalesAndPurchases({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: financialAnalysisDateRange.fromDate,
                  toDate: financialAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getCashFlowAnalysis({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: financialAnalysisDateRange.fromDate,
                  toDate: financialAnalysisDateRange.toDate,
               }),
            ),
         ]);
      } finally {
         setIsRefreshing((prev) => ({ ...prev, financial: false }));
      }
   }, [
      financialAnalysisDateRange.fromDate,
      financialAnalysisDateRange.toDate,
      dispatch,
      user?.loginhistory?.companyId,
   ]);

   const fetchPerformanceAnalysisData = useCallback(async () => {
      if (!user?.loginhistory?.companyId) return;

      setIsRefreshing((prev) => ({ ...prev, performance: true }));
      try {
         await Promise.all([
            dispatch(
               getProfitabilityAnalysisByCustomer({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: performanceAnalysisDateRange.fromDate,
                  toDate: performanceAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getTransactionSizeDistributionAnalysis({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: performanceAnalysisDateRange.fromDate,
                  toDate: performanceAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getAgingAnalysis({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: performanceAnalysisDateRange.fromDate,
                  toDate: performanceAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getYearOverYearComparison({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: performanceAnalysisDateRange.fromDate,
                  toDate: performanceAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getCustomerConcentrationAnalysis({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: performanceAnalysisDateRange.fromDate,
                  toDate: performanceAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getCityWiseSales({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: performanceAnalysisDateRange.fromDate,
                  toDate: performanceAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getSalesPaymentReceiveReport({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: performanceAnalysisDateRange.fromDate,
                  toDate: performanceAnalysisDateRange.toDate,
               }),
            ),
            dispatch(
               getPurchasePaidReport({
                  companyId: user?.loginhistory?.companyId,
                  fromDate: performanceAnalysisDateRange.fromDate,
                  toDate: performanceAnalysisDateRange.toDate,
               }),
            ),
         ]);
      } finally {
         setIsRefreshing((prev) => ({ ...prev, performance: false }));
      }
   }, [
      performanceAnalysisDateRange.fromDate,
      performanceAnalysisDateRange.toDate,
      dispatch,
      user?.loginhistory?.companyId,
   ]);

   const fetchGSTData = useCallback(async () => {
      if (!user?.loginhistory?.companyId) return;

      setIsRefreshing((prev) => ({ ...prev, GSTInfo: true }));
      try {
         await Promise.all([
            // Add GST related API calls here if needed
         ]);
      } finally {
         setIsRefreshing((prev) => ({ ...prev, GSTInfo: false }));
      }
   }, [gstInfo.fromDate, gstInfo.toDate, dispatch, user?.loginhistory?.companyId]);

   const ColumnVisibilityModal = ({ isOpen, onClose, columnVisibility, setColumnVisibility, user }) => {
      const [localVisibility, setLocalVisibility] = useState(columnVisibility);
      const companyId = Number(localStorage.getItem('companyId'));

      // ADD THIS - Get theme mode
      const { colorMode } = useColorMode();
      const isDark = colorMode === 'dark';

      useEffect(() => {
         if (isOpen) {
            setLocalVisibility(columnVisibility);
         }
      }, [isOpen, columnVisibility]);

      useEffect(() => {
         const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
               onClose();
            }
         };

         document.addEventListener('keydown', handleEscKey);

         return () => {
            document.removeEventListener('keydown', handleEscKey);
         };
      }, [isOpen, onClose]);

      const handleSave = async () => {
         try {
            const updatedViewJson = user.viewJson
               ? user.viewJson.map((item) =>
                    item.companyId === companyId ? { ...item, dashboard: localVisibility } : item,
                 )
               : [{ companyId, dashboard: localVisibility }];

            await dispatch(
               postColumnsHideShow({
                  userId: user?.id,
                  viewJson: updatedViewJson,
               }),
            );

            setColumnVisibility(localVisibility);
            localStorage.setItem('dashboardColumnVisibility', JSON.stringify(localVisibility));
            onClose();
         } catch (error) {
            console.log(error);
         }
      };

      // ADD THIS - Conditional render check
      if (!isOpen) return null;

      return (
         <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
            {/* Overlay with blur */}
            <div
               className={`absolute inset-0 transition-colors duration-300 ${isDark ? 'bg-black/70' : 'bg-black/40'}`}
               onClick={onClose}
            />

            {/* Modal Content */}
            <div
               className={`relative w-full max-w-2xl transform transition-all duration-300 ${
                  isDark
                     ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
                     : 'bg-gradient-to-br from-white via-gray-50 to-white'
               } rounded-2xl shadow-2xl border ${
                  isDark ? 'border-slate-700/50' : 'border-gray-200/50'
               } overflow-hidden flex flex-col max-h-[80vh]`}>
               {/* Header - Fixed */}
               <div
                  className={`relative px-8 py-6 border-b flex-shrink-0 ${
                     isDark
                        ? 'border-slate-700/50 bg-gradient-to-r from-slate-800/50 to-slate-900/50'
                        : 'border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50'
                  }`}>
                  <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                     Show/Hide Dashboard Widgets
                  </h2>
                  <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                     Customize which widgets appear on your dashboard
                  </p>
                  <button
                     onClick={onClose}
                     className={`absolute top-6 right-6 p-2 rounded-lg transition-all duration-200 ${
                        isDark
                           ? 'hover:bg-slate-700/50 text-slate-400 hover:text-white'
                           : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
                     }`}>
                     <X className="w-5 h-5" />
                  </button>
               </div>

               {/* Body - Scrollable */}
               <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="grid grid-cols-2 gap-4">
                     {Object.entries(localVisibility).map(([key, value]) => (
                        <div
                           key={key}
                           className={`group relative p-3 rounded-xl transition-all duration-300 border-2 ${
                              value
                                 ? isDark
                                    ? 'bg-gradient-to-br from-blue-900/20 to-indigo-900/20 border-blue-500/50 shadow-lg shadow-blue-500/10'
                                    : 'bg-gradient-to-br from-blue-50/50 to-indigo-50/50 border-blue-400/50 shadow-lg shadow-blue-200/30'
                                 : isDark
                                 ? 'bg-slate-800/30 border-slate-700/30 hover:border-slate-600/50'
                                 : 'bg-white/50 border-gray-300/30 hover:border-gray-400/50'
                           } cursor-pointer hover:scale-[1.02]`}
                           onClick={() => setLocalVisibility((prev) => ({ ...prev, [key]: !prev[key] }))}>
                           {/* Toggle Switch */}
                           <div className="flex items-center justify-between">
                              <label
                                 htmlFor={key}
                                 className={`font-semibold text-base cursor-pointer transition-colors duration-200 ${
                                    isDark ? 'text-white' : 'text-gray-900'
                                 }`}>
                                 {COLUMN_DISPLAY_NAMES[key] || key}
                              </label>

                              <div
                                 className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 ${
                                    value
                                       ? isDark
                                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/30'
                                          : 'bg-gradient-to-r from-blue-500 to-indigo-500 shadow-lg shadow-blue-400/30'
                                       : isDark
                                       ? 'bg-slate-700'
                                       : 'bg-gray-300'
                                 }`}>
                                 <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 shadow-md ${
                                       value ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                 />
                              </div>
                           </div>

                           {/* Status indicator */}
                           <div
                              className={`mt-3 flex items-center gap-2 text-xs font-medium ${
                                 value
                                    ? isDark
                                       ? 'text-blue-400'
                                       : 'text-blue-600'
                                    : isDark
                                    ? 'text-slate-500'
                                    : 'text-gray-500'
                              }`}>
                              <div
                                 className={`h-1.5 w-1.5 rounded-full ${
                                    value
                                       ? isDark
                                          ? 'bg-blue-400 shadow-lg shadow-blue-400/50'
                                          : 'bg-blue-500 shadow-lg shadow-blue-400/50'
                                       : isDark
                                       ? 'bg-slate-600'
                                       : 'bg-gray-400'
                                 }`}
                              />
                              {value ? 'Visible' : 'Hidden'}
                           </div>
                        </div>
                     ))}
                  </div>
               </div>

               {/* Footer - Fixed */}
               <div
                  className={`px-8 py-5 border-t flex justify-end gap-3 flex-shrink-0 ${
                     isDark
                        ? 'border-slate-700/50 bg-gradient-to-r from-slate-800/50 to-slate-900/50'
                        : 'border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50'
                  }`}>
                  <button
                     onClick={onClose}
                     className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${
                        isDark
                           ? 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                           : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                     }`}>
                     Cancel
                  </button>
                  <button
                     onClick={handleSave}
                     className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-lg ${
                        isDark
                           ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-500/30'
                           : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-blue-400/30'
                     }`}>
                     Save Changes
                  </button>
               </div>
            </div>
         </div>
      );
   };

   const handlePartyForm = async (amountType) => {
      try {
         setFormType(amountType === 2 ? 'Payables' : 'Receivables');

         // Directly Redux se data use karo
         setPartyData(accountData);
         setIsPartyFormOpen(true);
      } catch (err) {
         console.error('Error in handlePartyForm:', err);
      }
   };

   useEffect(() => {
      if (user?.loginhistory?.companyId) {
         const playload = {
            compId: user?.loginhistory?.companyId,
            accountTypes: ['K', 'E', 'C', 'D', 'BROKER', 'I', 'L'].join(','),
            // status: true,
            isReminder: false,
            // amountType: 1,
            ...(shouldShowCompanyDropdown && { companyIds: user?.loginhistory?.companyId }),
         };

         dispatch(getAccountForDashboard(playload));
      }
   }, [dispatch, shouldShowCompanyDropdown, user?.loginhistory?.companyId]);

   // Common refresh function for all data
   const handleRefreshAll = useCallback(async () => {
      setIsRefreshing((prev) => ({ ...prev, all: true }));
      dispatch(deleteLedgerEntries());
      dispatch(deleteOverAdjusted());
      dispatch(deleteExtraOutAdj());
      try {
         await Promise.all([fetchTopSalesData(), fetchTopPurchaseData(), fetchFinancialAnalysisData()]);
         // Mark all sections as loaded
         setLoadedSections({
            topSales: true,
            financialOverview: true,
            growthAnalysis: true,
            GSTInfo: true,
            performanceAnalysis: true,
         });
      } finally {
         setIsRefreshing((prev) => ({ ...prev, all: false }));
      }
   }, [fetchTopSalesData, fetchTopPurchaseData, fetchFinancialAnalysisData]);

   // Handlers for when sections become visible
   const handleTopSalesVisible = useCallback(() => {
      if (!loadedSections.topSales) {
         setLoadedSections((prev) => ({ ...prev, topSales: true }));
         Promise.all([fetchTopSalesData(), fetchTopPurchaseData()]);
      }
   }, [fetchTopSalesData, fetchTopPurchaseData, loadedSections.topSales]);

   const handleFinancialOverviewVisible = useCallback(() => {
      if (!loadedSections.financialOverview) {
         setLoadedSections((prev) => ({ ...prev, financialOverview: true }));
         fetchFinancialAnalysisData();
      }
   }, [fetchFinancialAnalysisData, loadedSections.financialOverview]);

   const handleGrowthAnalysisVisible = useCallback(() => {
      if (!loadedSections.growthAnalysis) {
         setLoadedSections((prev) => ({ ...prev, growthAnalysis: true }));
         fetchFinancialAnalysisData();
      }
   }, [fetchFinancialAnalysisData, loadedSections.growthAnalysis]);

   const handlePerformanceAnalysisVisible = useCallback(() => {
      if (!loadedSections.performanceAnalysis) {
         setLoadedSections((prev) => ({ ...prev, performanceAnalysis: true }));
         fetchPerformanceAnalysisData();
      }
   }, [fetchPerformanceAnalysisData, loadedSections.performanceAnalysis]);

   const handleGSTVisible = useCallback(() => {
      if (!loadedSections.GSTInfo) {
         setLoadedSections((prev) => ({ ...prev, GSTInfo: true }));
         fetchGSTData();
      }
   }, [fetchGSTData, loadedSections.GSTInfo]);

   // Add a loader for infinite scroll at the bottom
   const { loaderRef: bottomLoaderRef } = useScrollLoader(() => {
      // This will be called when user scrolls near the bottom
      // You can implement additional loading here if needed
      return Promise.resolve();
   });

   // Update date ranges when localStorage changes
   useEffect(() => {
      const handleStorageChange = () => {
         const newDateRange = getInitialDateRange();
         setPartyWiseSalesDateRange(newDateRange);
         setTopSalesDateRange(newDateRange);
         setTopPurchaseDateRange(newDateRange);
         setFinancialAnalysisDateRange(newDateRange);
         setPerformanceAnalysisDateRange(newDateRange);
      };

      window.addEventListener('storage', handleStorageChange);
      return () => {
         window.removeEventListener('storage', handleStorageChange);
      };
   }, []);

   const filterDataByType = (data, type) => {
      return data?.filter((item) => item.type === type) || [];
   };

   const topSalesData = filterDataByType(topFivePartyWiseSales, 'SALES');
   const topPurchaseData = filterDataByType(topFivePartyWiseSales, 'PURCHASE');

   const calculateTotalByType = (type) => {
      return topFivePartyWiseSales.filter((item) => item.type === type).reduce((sum, item) => sum + item.amount, 0);
   };

   const salesTotal = calculateTotalByType('SALES');
   const purchaseTotal = calculateTotalByType('PURCHASE');
   const salesReturnTotal = calculateTotalByType('SALES_RETURN');
   const purchaseReturnTotal = calculateTotalByType('PURCHASE_RETURN');

   const payables = accountData
      .filter((account) => account.amt > 0 && !account.name.toString().toLowerCase().includes('gst'))
      .reduce((sum, account) => sum + account.amt, 0);

   const receivables = Math.abs(
      accountData
         .filter((account) => account.amt < 0 && !account.name.toString().toLowerCase().includes('gst'))
         .reduce((sum, account) => sum + account.amt, 0),
   );
   const navigate = useNavigate();

   const formatAmount = (amount) => {
      if (amount === null || amount === undefined)
         return (
            <>
               ₹0
               <Text as="span" fontSize={{ base: '9px', md: '10px' }}>
                  .00
               </Text>
            </>
         );

      // Convert to number if string
      const num = typeof amount === 'string' ? parseFloat(amount) : amount;

      // Handle invalid numbers
      if (isNaN(num))
         return (
            <>
               ₹0
               <Text as="span" fontSize={{ base: '9px', md: '10px' }}>
                  .00
               </Text>
            </>
         );

      // For very large numbers (more than 100 crore), use compact format
      // if (num >= 1e12) {
      //    const value = (num / 1e12).toFixed(2);
      //    const [integerPart, decimalPart] = value.split('.');
      //    return (
      //       <>
      //          ₹{integerPart}
      //          <Text as="span" fontSize={{ base: '9px', md: '10px' }}>
      //             .{decimalPart}
      //          </Text>
      //          T
      //       </>
      //    );
      // } else if (num >= 1e9) {
      //    const value = (num / 1e9).toFixed(2);
      //    const [integerPart, decimalPart] = value.split('.');
      //    return (
      //       <>
      //          ₹{integerPart}
      //          <Text as="span" fontSize={{ base: '9px', md: '10px' }}>
      //             .{decimalPart}
      //          </Text>
      //          B
      //       </>
      //    );
      // } else if (num >= 1e7) {
      //    const value = (num / 1e7).toFixed(2);
      //    const [integerPart, decimalPart] = value.split('.');
      //    return (
      //       <>
      //          ₹{integerPart}
      //          <Text as="span" fontSize={{ base: '9px', md: '10px' }}>
      //             .{decimalPart}
      //          </Text>
      //          Cr
      //       </>
      //    );
      // }

      // For all normal amounts, use Indian number formatting with commas
      const formatted = num.toLocaleString('en-IN', {
         maximumFractionDigits: 2,
         minimumFractionDigits: 2,
      });

      const [integerPart, decimalPart] = formatted.split('.');

      return (
         <>
            ₹{integerPart}
            <Text as="span" fontSize={{ base: '13px', md: '15px' }}>
               .{decimalPart}
            </Text>
         </>
      );
   };

   //   const formatAmount = (amount) => {
   //    if (amount === null || amount === undefined) return '₹0.00';

   //    // Convert to number if string
   //    const num = typeof amount === 'string' ? parseFloat(amount) : amount;

   //    // Handle invalid numbers
   //    if (isNaN(num)) return '₹0.00';

   //    // For very large numbers (more than 100 crore), use compact format
   //    if (num >= 1e12) {
   //       return `₹${(num / 1e12).toFixed(2)}T`; // Trillion
   //    } else if (num >= 1e9) {
   //       return `₹${(num / 1e9).toFixed(2)}B`; // Billion (Arab)
   //    } else if (num >= 1e9) {
   //       return `₹${(num / 1e7).toFixed(2)}Cr`; // Crores
   //    }

   //    // For all normal amounts, use Indian number formatting with commas
   //    return `₹${num.toLocaleString('en-IN', {
   //       maximumFractionDigits: 2,
   //       minimumFractionDigits: 2,
   //    })}`;
   // };

   const statsData = [
      {
         label: 'Sales',
         amount: salesTotal,
         isPositive: true,
         icon: ShoppingCart,
         gradient: 'from-blue-500 to-cyan-500',
         bgLight: 'bg-gradient-to-br from-blue-50 to-cyan-50',
         bgDark: 'bg-gradient-to-br from-blue-900/20 to-cyan-900/20',
         iconColor: 'text-white',
         trendColor: 'text-emerald-400',
         trendBg: 'bg-emerald-500/15',
         route: '/admin/sales/invoice',
      },
      {
         label: 'Purchase',
         amount: purchaseTotal,
         isPositive: false,
         icon: Package,
         gradient: 'from-purple-500 to-violet-500',
         bgLight: 'bg-gradient-to-br from-purple-50 to-violet-50',
         bgDark: 'bg-gradient-to-br from-purple-900/20 to-violet-900/20',
         iconColor: 'text-white',
         trendColor: 'text-rose-400',
         trendBg: 'bg-rose-500/15',
         route: '/admin/purchase/invoice',
      },
      {
         label: 'Sales Return',
         amount: salesReturnTotal,
         isPositive: false,
         icon: RotateCcw,
         gradient: 'from-amber-500 to-orange-500',
         bgLight: 'bg-gradient-to-br from-amber-50 to-orange-50',
         bgDark: 'bg-gradient-to-br from-amber-900/20 to-orange-900/20',
         iconColor: 'text-white',
         trendColor: 'text-rose-400',
         trendBg: 'bg-rose-500/15',
         route: '/admin/sales/return',
      },
      {
         label: 'Purchase Return',
         amount: purchaseReturnTotal,
         isPositive: true,
         icon: Undo2,
         gradient: 'from-rose-500 to-pink-500',
         bgLight: 'bg-gradient-to-br from-rose-50 to-pink-50',
         bgDark: 'bg-gradient-to-br from-rose-900/20 to-pink-900/20',
         iconColor: 'text-white',
         trendColor: 'text-emerald-400',
         trendBg: 'bg-emerald-500/15',
         route: '/admin/purchase/return',
      },
      {
         label: 'Receivables',
         amount: receivables,
         isPositive: true,
         icon: Wallet,
         gradient: 'from-emerald-500 to-green-500',
         bgLight: 'bg-gradient-to-br from-emerald-50 to-green-50',
         bgDark: 'bg-gradient-to-br from-emerald-900/20 to-green-900/20',
         iconColor: 'text-white',
         trendColor: 'text-emerald-400',
         trendBg: 'bg-emerald-500/15',
         amountType: 1,
      },
      {
         label: 'Payables',
         amount: payables,
         isPositive: false,
         icon: CreditCard,
         gradient: 'from-teal-500 to-cyan-500',
         bgLight: 'bg-gradient-to-br from-teal-50 to-cyan-50',
         bgDark: 'bg-gradient-to-br from-teal-900/20 to-cyan-900/20',
         iconColor: 'text-white',
         trendColor: 'text-rose-400',
         trendBg: 'bg-rose-500/15',
         amountType: 2,
      },
   ];

   const [isActivating, setIsActivating] = useState(false);

   useEffect(() => {
      if (user?.askCompanyActivation && user?.companies?.length > 0) {
         setShowCompanyActivationModal(true);
      }
   }, [user?.askCompanyActivation, user?.companies]);

   // Handle company selection
   const handleCompanySelect = (companyId) => {
      setSelectedCompanies((prev) =>
         prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId],
      );
   };

   const handleCompanyActivation = async () => {
      try {
         setIsActivating(true);
         const formData = { ids: selectedCompanies };

         const result = await dispatch(UpdateBulkStatus(formData));
         await dispatch(getCompanies());

         // Use the first selected company ID instead of first company from list
         const selectedCompanyId = selectedCompanies[0];
         dispatch(updateUserHistory({ companyId: selectedCompanyId, code: '' }));

         if (result && result.code === 1) {
            setShowCompanyActivationModal(false);
            setSelectedCompanies([]);
         }
      } catch (error) {
         console.error('Failed to activate companies:', error);
      } finally {
         setIsActivating(false);
      }
   };

   const isAdmin = user?.isAdmin === true;

   return (
      <>
         {/* Company Activation Modal - SEPARATE FIXED LAYER */}
         {showCompanyActivationModal && (
            <div
               className={`fixed inset-0 ${
                  isDark ? 'bg-slate-900/80' : 'bg-slate-900/50'
               } backdrop-blur-sm flex items-center justify-center p-4 z-[1002]`}>
               <div
                  className={`${
                     isDark ? 'bg-slate-800' : 'bg-white'
                  } rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col`}>
                  {/* Header */}
                  <div
                     className={`sticky top-0 ${
                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                     } border-b p-6 flex items-center justify-between z-10 flex-shrink-0`}>
                     <div>
                        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                           Activate Companies
                        </h2>
                        <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'} mt-1`}>
                           Select companies to activate for your account
                        </p>
                     </div>
                     {/* Close Button (commented out) */}
                     {/* <button
                    onClick={() => setShowCompanyActivationModal(false)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        isDark ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-400'
                    }`}>
                    <span className="text-2xl">×</span>
                </button> */}
                  </div>

                  {/* Content */}
                  <div className="p-6 overflow-y-auto flex-1">
                     {/* Warning Banner */}
                     <div
                        className={`mb-6 p-4 rounded-lg border ${
                           isDark ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'
                        }`}>
                        <div className={`flex items-start gap-2 ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                           <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                 fillRule="evenodd"
                                 d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                 clipRule="evenodd"
                              />
                           </svg>
                           <p className="text-md font-medium">
                              This action can only be performed once. You cannot make changes until you purchase a new
                              package.
                           </p>
                        </div>
                     </div>

                     {/* Companies List */}
                     <div className="space-y-4">
                        {user?.companies?.map((company) => (
                           <div
                              key={company.id}
                              className={`border rounded-xl p-5 hover:shadow-md transition-all duration-200 cursor-pointer ${
                                 selectedCompanies.includes(company.id)
                                    ? isDark
                                       ? 'border-blue-500 bg-blue-900/20'
                                       : 'border-blue-500 bg-blue-50'
                                    : isDark
                                    ? 'border-slate-700 bg-slate-800 hover:bg-slate-750'
                                    : 'border-slate-200 bg-white'
                              }`}
                              onClick={() => handleCompanySelect(company.id)}>
                              <div className="flex items-center justify-between">
                                 <div className="flex items-center gap-4">
                                    <div
                                       className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                          selectedCompanies.includes(company.id)
                                             ? 'bg-blue-500 border-blue-500'
                                             : isDark
                                             ? 'border-slate-500'
                                             : 'border-slate-300'
                                       }`}>
                                       {selectedCompanies.includes(company.id) && (
                                          <Check className="w-3 h-3 text-white" />
                                       )}
                                    </div>
                                    <div>
                                       <h3
                                          className={`text-lg font-semibold ${
                                             isDark ? 'text-white' : 'text-slate-900'
                                          }`}>
                                          {company.name}
                                       </h3>
                                       <div className="flex items-center gap-4 mt-2 text-sm">
                                          <div
                                             className={`flex items-center gap-1 ${
                                                isDark ? 'text-slate-300' : 'text-slate-600'
                                             }`}>
                                             <FileText className="w-4 h-4" />
                                             <span>GST: {company.gstRegNo}</span>
                                          </div>
                                          <div
                                             className={`flex items-center gap-1 ${
                                                isDark ? 'text-slate-300' : 'text-slate-600'
                                             }`}>
                                             <CreditCard className="w-4 h-4" />
                                             <span>PAN: {company.panNo}</span>
                                          </div>
                                       </div>
                                    </div>
                                 </div>
                                 <div
                                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                       selectedCompanies.includes(company.id)
                                          ? isDark
                                             ? 'bg-blue-800 text-blue-200'
                                             : 'bg-blue-100 text-blue-700'
                                          : isDark
                                          ? 'bg-slate-700 text-slate-300'
                                          : 'bg-slate-100 text-slate-600'
                                    }`}>
                                    {selectedCompanies.includes(company.id) ? 'Selected' : 'Click to select'}
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>

                     {/* No Companies Message */}
                     {(!user?.companies || user.companies.length === 0) && (
                        <div className="text-center py-12">
                           <div
                              className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                                 isDark ? 'bg-slate-700' : 'bg-slate-100'
                              }`}>
                              <Building2 className={`w-8 h-8 ${isDark ? 'text-slate-400' : 'text-slate-400'}`} />
                           </div>
                           <p className={`${isDark ? 'text-slate-300' : 'text-slate-500'} text-lg font-medium mb-2`}>
                              No Companies Found
                           </p>
                           <p className={isDark ? 'text-slate-400' : 'text-slate-400'}>
                              You don't have any companies to activate.
                           </p>
                        </div>
                     )}
                  </div>

                  {/* Footer with Action Buttons */}
                  <div
                     className={`sticky bottom-0 border-t p-6 flex items-center justify-between flex-shrink-0 ${
                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                     }`}>
                     <div>
                        <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                           {selectedCompanies.length} compan{selectedCompanies.length !== 1 ? 'ies' : 'y'} selected
                        </p>
                     </div>
                     <div className="flex items-center gap-3">
                        {/* Cancel Button (commented out) */}
                        {/* <button
                        onClick={() => setShowCompanyActivationModal(false)}
                        className={`px-5 py-2.5 rounded-lg font-medium transition-colors ${
                            isDark 
                                ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' 
                                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}>
                        Cancel
                    </button> */}
                        <button
                           onClick={handleCompanyActivation}
                           disabled={selectedCompanies.length === 0}
                           className={`px-5 py-2.5 rounded-lg font-medium transition-colors ${
                              selectedCompanies.length > 0
                                 ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                 : isDark
                                 ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                 : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                           }`}>
                           Activate Selected Companies
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         )}
         {isAdmin && (
            <button
               onClick={() => setShowDrawer(true)}
               className="fixed top-1/3 right-0 -translate-y-1/2 z-40 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-l-lg shadow-lg">
               <User className="w-5 h-5" />
            </button>
         )}

         {/* Drawer */}
         <UserDetailsPage isOpen={showDrawer} onClose={() => setShowDrawer(false)} />
         <div className={`pt-4 h-screen overflow-x-hidden ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* Header */}
            <div className="flex justify-between items-center px-4 mt-10">
               <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Dashboard</h1>
               <div className="flex gap-2">
                  <button
                     className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                        isDark ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                     }`}
                     onClick={() => setIsColumnModalOpen(true)}>
                     <BsEye />
                  </button>
                  <button
                     onClick={handleRefreshAll}
                     disabled={isRefreshing.all}
                     className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors disabled:opacity-50 ${
                        isDark ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                     }`}>
                     <RepeatIcon className={isRefreshing.all ? 'animate-spin' : ''} />
                     {isRefreshing.all ? 'Refreshing All...' : ''}
                  </button>
               </div>
            </div>
            <div className="h-full max-h-[calc(100vh-140px)] overflow-y-auto pr-4">
               <div className={`transition-all duration-500 ${isDark ? 'bg-gray-900' : 'bg-slate-50'} p-6 sm:p-8`}>
                  {/* Stats Grid */}
                  {isInitialLoading ? (
                     <CardGridSkeleton count={6} />
                  ) : (
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5 sm:gap-6 lg:gap-7">
                        {statsData.map((stat, index) => {
                           const Icon = stat.icon;
                           return (
                              <div
                                 key={index}
                                 onClick={() => (stat.route ? navigate(stat.route) : handlePartyForm(stat.amountType))}
                                 className={`group relative overflow-hidden rounded-2xl transition-all duration-500 cursor-pointer ${
                                    isDark
                                       ? `${stat.bgDark} border border-gray-700/50 hover:border-gray-600`
                                       : `${stat.bgLight} border border-white shadow-xs hover:shadow-md`
                                 } hover:-translate-y-2 hover:scale-[1.02]`}>
                                 {/* Animated Gradient Border Effect */}
                                 <div
                                    className={`absolute inset-0 bg-gradient-to-r ${stat.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`}></div>

                                 {/* Subtle Shine Effect */}
                                 <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>

                                 <div className="relative p-5 sm:p-6 flex flex-col h-full">
                                    {/* Header Section */}
                                    <div className="flex items-center justify-between mb-4">
                                       {/* Modern Icon Container */}
                                       <div className="relative">
                                          <div
                                             className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:shadow-xl`}>
                                             <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                          </div>
                                          {/* Icon Glow Effect */}
                                          <div
                                             className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} rounded-xl blur-md opacity-30 group-hover:opacity-50 transition-opacity duration-500`}></div>
                                       </div>

                                       {/* Trend Indicator */}
                                       <div
                                          className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                                             stat.trendBg
                                          } backdrop-blur-sm border ${
                                             isDark ? 'border-gray-600/50' : 'border-white/50'
                                          }`}>
                                          <span className={`text-sm font-semibold ${stat.trendColor}`}>
                                             {stat.isPositive ? '↗' : '↘'}
                                          </span>
                                       </div>
                                    </div>

                                    {/* Content Section */}
                                    <div className="flex flex-col flex-grow justify-between space-y-4">
                                       <div className="space-y-2">
                                          {/* Label */}
                                          <div
                                             className={`text-sm font-medium transition-colors duration-300 ${
                                                isDark ? 'text-gray-300' : 'text-slate-600'
                                             }`}>
                                             {stat.label}
                                          </div>

                                          {/* Amount */}
                                          <div
                                             className={`text-base lg:text-lg xl:text-xl font-bold tracking-tight transition-colors duration-300 whitespace-nowrap ${
                                                isDark ? 'text-white' : 'text-slate-900'
                                             }`}>
                                             {formatAmount(stat.amount)}
                                          </div>
                                       </div>

                                       {/* Enhanced Progress Bar */}
                                       <div className="space-y-2">
                                          <div
                                             className={`w-full rounded-full h-2 overflow-hidden backdrop-blur-sm ${
                                                isDark ? 'bg-gray-700/50' : 'bg-white/50'
                                             } border ${isDark ? 'border-gray-600/30' : 'border-slate-200/50'}`}>
                                             <div
                                                className={`h-2 rounded-full bg-gradient-to-r ${stat.gradient} shadow-sm transition-all duration-1000 ease-out`}
                                                style={{
                                                   width: `${Math.min(Math.abs(stat.percentage) * 2, 100)}%`,
                                                }}></div>
                                          </div>

                                          {/* Percentage Text */}
                                       </div>
                                    </div>

                                    {/* Clickable Overlay Effect */}
                                    {stat.route && (
                                       <div className="absolute inset-0 bg-black/0 group-hover:bg-black/2 transition-colors duration-300 rounded-2xl"></div>
                                    )}
                                 </div>

                                 {/* Corner Accents */}
                                 <div
                                    className={`absolute top-0 right-0 w-12 h-12 bg-gradient-to-bl ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-tr-2xl`}></div>
                                 <div
                                    className={`absolute bottom-0 left-0 w-8 h-8 bg-gradient-to-tr ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-bl-2xl`}></div>
                              </div>
                           );
                        })}
                     </div>
                  )}

                  {/* GST Data Section */}
                  {((columnVisibility.GSTInfo && comapnySetting?.setting?.applyGST) || columnVisibility.whatsNew) && (
                     <SectionWrapper
                        onRefresh={fetchGSTData}
                        isLoading={isRefreshing.GSTInfo}
                        onVisible={handleGSTVisible}
                        isVisible={columnVisibility.GSTInfo || columnVisibility.whatsNew}
                        isDark={isDark}
                        skeletonComponent={
                           <div className="flex flex-col lg:flex-row gap-4 mb-6">
                              {columnVisibility.GSTInfo && comapnySetting?.setting?.applyGST && (
                                 <div className="flex-1 min-w-0">
                                    <ChartSkeleton height="65vh" />
                                 </div>
                              )}
                              {columnVisibility.whatsNew && (
                                 <div className="flex-1 min-w-0">
                                    <ChartSkeleton height="100%" />
                                 </div>
                              )}
                           </div>
                        }>
                        <div className="flex flex-col lg:flex-row gap-4 mb-6">
                           {columnVisibility.GSTInfo &&
                              comapnySetting?.setting?.applyGST &&
                              user?.packageData?.[0]?.isDashboardGst && (
                                 <div className="flex-[2] min-w-0">
                                    {' '}
                                    {/* flex-[2] for more width */}
                                    <ItcLedger title="GST Data" height="65vh" />
                                 </div>
                              )}
                           {columnVisibility.whatsNew && (
                              <div className="flex-1 min-w-0">
                                 {' '}
                                 {/* flex-1 for less width */}
                                 <WeeklyRevenue height="100%" />
                              </div>
                           )}
                        </div>
                     </SectionWrapper>
                  )}

                  {/* Top Sales and Purchase Section */}
                  {(columnVisibility.topSales || columnVisibility.topPurchase) && (
                     <SectionWrapper
                        onRefresh={() => Promise.all([fetchTopSalesData(), fetchTopPurchaseData()])}
                        isLoading={isRefreshing.topSales || isRefreshing.topPurchase}
                        onVisible={handleTopSalesVisible}
                        isVisible={columnVisibility.topSales || columnVisibility.topPurchase}
                        isDark={isDark}
                        skeletonComponent={
                           <div className="flex flex-col lg:flex-row gap-4">
                              {columnVisibility.topSales && (
                                 <div className="flex-1">
                                    <TableSkeleton height="100%" rowCount={5} />
                                 </div>
                              )}
                              {columnVisibility.topPurchase && (
                                 <div className="flex-1">
                                    <TableSkeleton height="100%" rowCount={5} />
                                 </div>
                              )}
                           </div>
                        }>
                        <div className="flex flex-col lg:flex-row gap-4">
                           {columnVisibility.topSales && (
                              <div className="flex-1">
                                 <TopFiveSalesAndPurchase
                                    title="Top Sales"
                                    datePartyWiseSalesRange={datePartyWiseSalesRange}
                                    setPartyWiseSalesDateRange={setPartyWiseSalesDateRange}
                                    data={topSalesData}
                                    height="100%"
                                    reload={fetchTopSalesData}
                                 />
                              </div>
                           )}
                           {columnVisibility.topPurchase && (
                              <div className="flex-1">
                                 <TopFiveSalesAndPurchase
                                    title="Top Purchases"
                                    data={topPurchaseData}
                                    datePartyWiseSalesRange={datePartyWiseSalesRange}
                                    setPartyWiseSalesDateRange={setPartyWiseSalesDateRange}
                                    height="100%"
                                    reload={fetchTopPurchaseData}
                                 />
                              </div>
                           )}
                        </div>
                     </SectionWrapper>
                  )}

                  {/* Financial Overview Section */}
                  {(columnVisibility.rojmel || columnVisibility.bankAccounts) && (
                     <SectionWrapper
                        onRefresh={fetchFinancialAnalysisData}
                        isLoading={isRefreshing.financial}
                        onVisible={handleFinancialOverviewVisible}
                        isVisible={columnVisibility.rojmel || columnVisibility.bankAccounts}
                        isDark={isDark}
                        skeletonComponent={
                           <div className="flex flex-col lg:flex-row gap-4 mt-5">
                              {columnVisibility.rojmel && (
                                 <div className="w-600px">
                                    <ChartSkeleton height="400px" />
                                 </div>
                              )}
                              {columnVisibility.bankAccounts && (
                                 <div className="flex-1 w-300px">
                                    <ChartSkeleton height="100%" />
                                 </div>
                              )}
                           </div>
                        }>
                        <div className="flex flex-col lg:flex-row gap-4 mt-5">
                           {columnVisibility.rojmel && user?.packageData?.[0]?.isRojmel && (
                              <div className="w-600px">
                                 <Rojmel
                                    datePartyWiseSalesRange={datePartyWiseSalesRange}
                                    setPartyWiseSalesDateRange={setPartyWiseSalesDateRange}
                                 />
                              </div>
                           )}
                           {columnVisibility.bankAccounts && (
                              <div className="flex-1 w-300px">
                                 <BankAccountsCard
                                    height="100%"
                                    data={cashAndBankBalance}
                                    financialAnalysisDateRange={financialAnalysisDateRange}
                                    user={user}
                                 />
                              </div>
                           )}
                        </div>
                     </SectionWrapper>
                  )}

                  {/* Growth Rate Section */}
                  {columnVisibility.growthRate && (
                     <SectionWrapper
                        onRefresh={fetchFinancialAnalysisData}
                        isLoading={isRefreshing.financial}
                        onVisible={handleGrowthAnalysisVisible}
                        isVisible={columnVisibility.growthRate}
                        isDark={isDark}
                        skeletonComponent={
                           <div className="flex flex-col gap-4">
                              <div className="w-full">
                                 <TableSkeleton height={400} rowCount={8} />
                              </div>
                           </div>
                        }>
                        <div className="flex flex-col gap-4">
                           <div className="w-full">
                              <GrowthRateTable
                                 title="Growth Rate"
                                 data={growthRate}
                                 height={400}
                                 financialAnalysisDateRange={financialAnalysisDateRange}
                                 user={user}
                              />
                           </div>
                        </div>
                     </SectionWrapper>
                  )}

                  {/* Performance Analysis Section */}
                  {(columnVisibility.totalSaleVsPurchase ||
                     columnVisibility.profitabilityByCustomer ||
                     columnVisibility.cashFlowAnalysis ||
                     columnVisibility.transactionSizeDistribution ||
                     columnVisibility.agingAnalysis ||
                     columnVisibility.cityWiseSalesAnalysis ||
                     columnVisibility.purchasePaidReport ||
                     columnVisibility.salesPaymentReceiveReport) && (
                     <SectionWrapper
                        onRefresh={fetchPerformanceAnalysisData}
                        isLoading={isRefreshing.performance}
                        onVisible={handlePerformanceAnalysisVisible}
                        isVisible={
                           columnVisibility.totalSaleVsPurchase ||
                           columnVisibility.profitabilityByCustomer ||
                           columnVisibility.cashFlowAnalysis ||
                           columnVisibility.transactionSizeDistribution ||
                           columnVisibility.agingAnalysis ||
                           columnVisibility.cityWiseSalesAnalysis ||
                           columnVisibility.purchasePaidReport ||
                           columnVisibility.salesPaymentReceiveReport
                        }
                        isDark={isDark}
                        skeletonComponent={
                           <>
                              {/* Sales vs Purchase and City Wise */}
                              {(columnVisibility.totalSaleVsPurchase || columnVisibility.cityWiseSalesAnalysis) && (
                                 <div className="flex flex-col lg:flex-row gap-4 mb-6">
                                    {columnVisibility.totalSaleVsPurchase && (
                                       <div className="flex-1 min-w-0">
                                          <ChartSkeleton height="400px" />
                                       </div>
                                    )}
                                    {columnVisibility.cityWiseSalesAnalysis && (
                                       <div className="flex-1 min-w-0">
                                          <ChartSkeleton height="400px" />
                                       </div>
                                    )}
                                 </div>
                              )}

                              {/* Profitability and Cash Flow */}
                              {(columnVisibility.profitabilityByCustomer || columnVisibility.cashFlowAnalysis) && (
                                 <div className="flex flex-col lg:flex-row gap-4 mb-6">
                                    {columnVisibility.profitabilityByCustomer && (
                                       <div className="flex-1 min-w-0">
                                          <ChartSkeleton height="65vh" />
                                       </div>
                                    )}
                                    {columnVisibility.cashFlowAnalysis && (
                                       <div className="flex-1 min-w-0">
                                          <ChartSkeleton height="65vh" />
                                       </div>
                                    )}
                                 </div>
                              )}

                              {/* Transaction Size and Aging */}
                              {(columnVisibility.transactionSizeDistribution || columnVisibility.agingAnalysis) && (
                                 <div className="flex flex-col lg:flex-row gap-4 mb-6">
                                    {columnVisibility.transactionSizeDistribution && (
                                       <div className="flex-1 min-w-0">
                                          <ChartSkeleton height="65vh" />
                                       </div>
                                    )}
                                    {columnVisibility.agingAnalysis && (
                                       <div className="flex-1 min-w-0">
                                          <ChartSkeleton height="65vh" />
                                       </div>
                                    )}
                                 </div>
                              )}

                              {/* Purchase Paid and Sales Payment */}
                              {(columnVisibility.purchasePaidReport || columnVisibility.salesPaymentReceiveReport) && (
                                 <div className="flex flex-col lg:flex-row gap-4">
                                    {columnVisibility.purchasePaidReport && (
                                       <div className="flex-1 min-w-0">
                                          <ChartSkeleton height="400px" />
                                       </div>
                                    )}
                                    {columnVisibility.salesPaymentReceiveReport && (
                                       <div className="flex-1 min-w-0">
                                          <ChartSkeleton height="400px" />
                                       </div>
                                    )}
                                 </div>
                              )}
                           </>
                        }>
                        {/* Sales vs Purchase and City Wise */}
                        {(columnVisibility.totalSaleVsPurchase || columnVisibility.cityWiseSalesAnalysis) && (
                           <div className="flex flex-col lg:flex-row gap-4 mb-6">
                              {columnVisibility.totalSaleVsPurchase && (
                                 <div className="flex-1 min-w-0">
                                    <SalesVsPurchaseChart
                                       data={totalSalesAndPurchases}
                                       title="Total Sales vs Purchase"
                                       height="100%"
                                       financialAnalysisDateRange={financialAnalysisDateRange}
                                       user={user}
                                    />
                                 </div>
                              )}
                              {columnVisibility.cityWiseSalesAnalysis && (
                                 <div className="flex-1 min-w-0">
                                    <CustomerConcentrationChart
                                       data={customerConcentrationAnalysis}
                                       height="400px"
                                       financialAnalysisDateRange={financialAnalysisDateRange}
                                       user={user}
                                    />
                                 </div>
                              )}
                           </div>
                        )}

                        {/* Profitability and Cash Flow */}
                        {(columnVisibility.profitabilityByCustomer || columnVisibility.cashFlowAnalysis) && (
                           <div className="flex flex-col lg:flex-row gap-4 mb-6">
                              {columnVisibility.profitabilityByCustomer && (
                                 <div className="flex-1 min-w-0">
                                    <ProfitabilityByCustomerChart
                                       data={profitabilityAnalysisByCustomer}
                                       height="65vh"
                                       financialAnalysisDateRange={financialAnalysisDateRange}
                                       user={user}
                                    />
                                 </div>
                              )}
                              {columnVisibility.cashFlowAnalysis && (
                                 <div className="flex-1 min-w-0">
                                    <CashFlowAnalysisChart
                                       data={cashFlowAnalysis}
                                       height="65vh"
                                       financialAnalysisDateRange={financialAnalysisDateRange}
                                       user={user}
                                    />
                                 </div>
                              )}
                           </div>
                        )}

                        {/* Transaction Size and Aging */}
                        {(columnVisibility.transactionSizeDistribution || columnVisibility.agingAnalysis) && (
                           <div className="flex flex-col lg:flex-row gap-4 mb-6">
                              {columnVisibility.transactionSizeDistribution && (
                                 <div className="flex-1 min-w-0">
                                    <TransactionSizeDistributionChart
                                       data={transactionSizeDistributionAnalysis}
                                       height="65vh"
                                       financialAnalysisDateRange={financialAnalysisDateRange}
                                       user={user}
                                    />
                                 </div>
                              )}
                              {columnVisibility.agingAnalysis && (
                                 <div className="flex-1 min-w-0">
                                    <AgingBarChart
                                       data={agingAnalysis}
                                       height="65vh"
                                       financialAnalysisDateRange={financialAnalysisDateRange}
                                       user={user}
                                    />
                                 </div>
                              )}
                           </div>
                        )}

                        {/* Purchase Paid and Sales Payment */}
                        {(columnVisibility.purchasePaidReport || columnVisibility.salesPaymentReceiveReport) && (
                           <div className="flex flex-col lg:flex-row gap-4">
                              {columnVisibility.purchasePaidReport && (
                                 <div className="flex-1 min-w-0">
                                    <PurchasePaidChart
                                       data={purchasePaidReport}
                                       height="400px"
                                       financialAnalysisDateRange={financialAnalysisDateRange}
                                       user={user}
                                    />
                                 </div>
                              )}
                              {columnVisibility.salesPaymentReceiveReport && (
                                 <div className="flex-1 min-w-0">
                                    <SalesPaymentRadialChart
                                       data={salesPaymentReceiveReport}
                                       height="400px"
                                       financialAnalysisDateRange={financialAnalysisDateRange}
                                       user={user}
                                    />
                                 </div>
                              )}
                           </div>
                        )}
                     </SectionWrapper>
                  )}

                  {/* Bottom loader for infinite scroll */}
                  <div ref={bottomLoaderRef} style={{ height: '20px' }} />
               </div>
            </div>
         </div>

         <ColumnVisibilityModal
            isOpen={isColumnModalOpen}
            onClose={() => setIsColumnModalOpen(false)}
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
            user={user}
         />

         {isPartyFormOpen && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[999]">
               <PartyForm data={partyData} onClose={() => setIsPartyFormOpen(false)} type={formType} />
            </div>
         )}
      </>
   );
}
